import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  UserIcon, 
  MailIcon, 
  CalendarIcon, 
  ShieldIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  PhoneIcon,
  GraduationCapIcon,
  BookOpenIcon
} from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import learningLevelsData from '../../../data/learningLevels.json';

const StudentProfile = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('StudentProfile mounted with studentId:', studentId);
    console.log('Current URL:', window.location.href);
    console.log('Current pathname:', window.location.pathname);
    fetchStudentProfile();
  }, [studentId]);

  // Helper function to translate level name
  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  const fetchStudentProfile = async () => {
    setLoading(true);
    console.log('Fetching student profile for studentId:', studentId);
    try {
      const response = await api.get(`/students/${studentId}`);
      console.log('Student profile response:', response.data);
      if (response.data.success) {
        // البيانات الحقيقية من API
        const studentData = response.data.data.student;
        console.log('Student data from API:', studentData);
        setStudent(studentData);
        console.log('Student data set successfully');
      } else {
        console.error('API returned success: false');
        toast.error(t('users.students.profileLoadError'), { duration: 3000, position: 'top-center' });
      }
    } catch (error) {
      console.error('Error fetching student profile:', error);
      console.error('Error details:', error.response?.data);
      toast.error(t('users.students.profileLoadError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return t('profile.notAvailable');
    const date = new Date(dateString);
    return date.toLocaleDateString(
      currentLanguage === 'ar' ? 'ar-SA' : (currentLanguage === 'en' ? 'en-US' : 'fr-FR'),
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    );
  };

  const formatDateOfBirth = (dateString) => {
    if (!dateString) return t('profile.notAvailable');
    const date = new Date(dateString);
    return date.toLocaleDateString(
      currentLanguage === 'ar' ? 'ar-SA' : (currentLanguage === 'en' ? 'en-US' : 'fr-FR'),
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }
    );
  };

  const getGenderText = (gender) => {
    switch (gender) {
      case 'male':
        return t('users.male');
      case 'female':
        return t('users.female');
      case 'other':
        return t('users.other');
      default:
        return t('profile.notAvailable');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('users.students.loadingProfile')}</h2>
          <p className="text-gray-600">{t('users.students.pleaseWait')}</p>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('users.students.studentNotFound')}</h2>
          <p className="text-gray-600">{t('users.students.studentNotFoundDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeftIcon className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          {t('common.back')}
        </button>
      </div>

      {/* Profile Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg p-6 text-white">
        <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <GraduationCapIcon className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">
              {student.first_name} {student.last_name}
            </h2>
            <p className="text-primary-100">
              {t('users.students.student')} - {student.student_number}
            </p>
            <p className="text-sm text-primary-200">
              {student.email}
            </p>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white shadow-lg rounded-lg border border-gray-200">
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Personal Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserIcon className={`w-5 h-5 text-primary-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('profile.personalInfo')}
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.firstName')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {student.first_name || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.lastName')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {student.last_name || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('common.email')}
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <span className="text-gray-900 font-medium">
                        {student.email || t('profile.notAvailable')}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('users.phone')}
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center">
                        <PhoneIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <span className="text-gray-900 font-medium">
                          {student.phone || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.dateOfBirth')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          <CalendarIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span className="text-gray-900 font-medium">
                            {student.date_of_birth ? formatDateOfBirth(student.date_of_birth) : t('profile.notAvailable')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.gender')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {getGenderText(student.gender)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Student Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <GraduationCapIcon className={`w-5 h-5 text-primary-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('users.students.studentInfo')}
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {/* Student Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.students.studentNumber')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {student.student_number || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>

                    {/* Student ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.students.studentId')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          #{student.id || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>

                    {/* Current Level */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.students.currentLevel')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          <BookOpenIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span className="text-gray-900 font-medium">
                            {translateLevelName(student.level_name) || t('profile.notAvailable')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Level Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.students.levelNumber')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {student.level_number || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>

                    {/* Account Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.students.status')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          {student.is_active ? (
                            <CheckCircleIcon className={`w-4 h-4 text-green-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          ) : (
                            <XCircleIcon className={`w-4 h-4 text-red-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          )}
                          <span className={`text-sm font-medium ${student.is_active ? 'text-green-600' : 'text-red-600'}`}>
                            {student.is_active ? t('users.students.active') : t('users.students.inactive')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Username */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.username')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {student.username || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Enrollment Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('users.students.enrollmentDate')}
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center">
                        <CalendarIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <span className="text-gray-900 font-medium">
                          {student.enrollment_date ? formatDate(student.enrollment_date) : t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Graduation Date */}
                  {student.graduation_date && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.students.graduationDate')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          <CalendarIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span className="text-gray-900 font-medium">
                            {formatDate(student.graduation_date)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Created At */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile.createdAt')}
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center">
                        <CalendarIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <span className="text-gray-900 font-medium">
                          {student.created_at ? formatDate(student.created_at) : t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Updated At */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile.updatedAt')}
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center">
                        <CalendarIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <span className="text-gray-900 font-medium">
                          {student.updated_at ? formatDate(student.updated_at) : t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Level Description */}
                {student.level_description && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <BookOpenIcon className={`w-5 h-5 text-primary-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t('users.students.levelDescription')}
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <p className="text-gray-900">
                        {student.level_description}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProfile;
